import { ADS_FUNNEL_AGE_RANGES, ADS_FUNNEL_GENDERS, isAdsCountryCode, } from '@wit-support/admin-contracts';
import { formatCountryList } from './lib/country.js';
/*
 * Значения настроек воронки с условиями (спека 2026-10-26 §3.1, план C1/C2):
 * `{ rules: [{ when: { countries?, ageRanges?, genders? }, value }], otherwise? }`
 * — первое совпавшее правило, иначе «во всех остальных случаях». Админка
 * только задаёт и показывает их; разрешают воронка и app-service.
 *
 * Здесь — чистые функции: какие измерения даёт каталог, можно ли значение
 * редактировать формой и сводка словами. Сравнение по канонической форме —
 * `sameFunnelValue` из `admin-contracts` (одна реализация, как в auto-ads).
 */
/** Возрастной диапазон словами («25–34», «65+»). */
export const AGE_RANGE_LABELS = {
    '18-24': '18–24',
    '25-34': '25–34',
    '35-44': '35–44',
    '45-54': '45–54',
    '55-64': '55–64',
    '65+': '65+',
};
/** Пол — подпись флажка. */
export const GENDER_LABELS = {
    female: 'Женщины',
    male: 'Мужчины',
    other: 'Другой пол',
};
const AGE_SET = new Set(ADS_FUNNEL_AGE_RANGES);
const GENDER_SET = new Set(ADS_FUNNEL_GENDERS);
const KNOWN_DIMENSIONS = new Set(['countries', 'ageRanges', 'genders']);
export const isAgeRange = (v) => AGE_SET.has(v);
export const isGender = (v) => GENDER_SET.has(v);
/**
 * Измерения из `catalog.dimensions`: только известные админке и в её порядке
 * (возраст — по возрастанию, пол — женщины / мужчины / другой пол). Каталог без
 * измерений (старый app-service) — условий нет.
 */
export function targetingDims(catalog) {
    const dims = catalog?.dimensions ?? [];
    const byKey = new Map(dims.map((d) => [d.key, d]));
    const values = (key) => new Set(byKey.get(key)?.values ?? []);
    const ages = values('ageRanges');
    const genders = values('genders');
    return {
        countries: byKey.has('countries'),
        ageRanges: ADS_FUNNEL_AGE_RANGES.filter((a) => ages.has(a)),
        genders: ADS_FUNNEL_GENDERS.filter((g) => genders.has(g)),
    };
}
export function hasTargeting(d) {
    return d.countries || d.ageRanges.length > 0 || d.genders.length > 0;
}
/** Значение с правилами (а не обычное). */
export function isTargetedValue(v) {
    return typeof v === 'object' && v !== null && Array.isArray(v.rules);
}
const RULE_KEYS = new Set(['when', 'value']);
const VALUE_KEYS = new Set(['rules', 'otherwise']);
const onlyKeys = (o, allowed) => Object.keys(o).every((k) => allowed.has(k));
/**
 * Правила можно показать в форме без потерь: каждое условие — по измерению,
 * которое есть в каталоге, и только известными значениями (страны — из
 * справочника админки), а у правила и значения нет полей, которых форма не
 * знает. Иначе форма молча выбросила бы часть значения — такое значение
 * только показывается и уходит обратно как было.
 */
export function isEditableTargeted(v, dims) {
    if (v.rules.length === 0 || !onlyKeys(v, VALUE_KEYS))
        return false;
    const ages = new Set(dims.ageRanges);
    const genders = new Set(dims.genders);
    return v.rules.every((r) => {
        if (!onlyKeys(r, RULE_KEYS))
            return false;
        const keys = Object.keys(r.when);
        if (keys.length === 0)
            return false;
        return keys.every((k) => {
            const list = r.when[k] ?? [];
            if (!KNOWN_DIMENSIONS.has(k) || list.length === 0)
                return false;
            if (k === 'countries')
                return dims.countries && list.every(isAdsCountryCode);
            if (k === 'ageRanges')
                return list.every((x) => ages.has(x));
            return list.every((g) => genders.has(g));
        });
    });
}
// ---------------------------------------------------------------------------
// Сводка словами
// ---------------------------------------------------------------------------
/**
 * Возраст словами; соседние диапазоны склеиваются: 25–34 + 35–44 → «25–44»,
 * 55–64 + 65+ → «55+».
 */
export function ageRangesText(ranges) {
    const idx = ADS_FUNNEL_AGE_RANGES.map((a, i) => (ranges.includes(a) ? i : -1)).filter((i) => i >= 0);
    const parts = [];
    let start = -1;
    let prev = -2;
    const flush = () => {
        if (start < 0)
            return;
        const from = ADS_FUNNEL_AGE_RANGES[start].split('-')[0].replace('+', '');
        const last = ADS_FUNNEL_AGE_RANGES[prev];
        if (last === '65+')
            parts.push(`${from}+`);
        else
            parts.push(start === prev ? AGE_RANGE_LABELS[last] : `${from}–${last.split('-')[1]}`);
    };
    for (const i of idx) {
        if (i !== prev + 1) {
            flush();
            start = i;
        }
        prev = i;
    }
    flush();
    return parts.join(', ');
}
const GENDER_WORDS = {
    female: 'женщины',
    male: 'мужчины',
    other: 'другой пол',
};
/** Условие правила словами: «США, Великобритания · 25–44 · пол: женщины». */
export function whenText(when) {
    const parts = [];
    const countries = when['countries'];
    if (countries && countries.length > 0)
        parts.push(formatCountryList(countries, 5).text);
    const ages = when['ageRanges'];
    if (ages && ages.length > 0) {
        const known = ages.filter(isAgeRange);
        parts.push(known.length === ages.length ? ageRangesText(known) : 'возраст не из списка');
    }
    const genders = when['genders'];
    if (genders && genders.length > 0) {
        const known = genders.filter(isGender);
        // «пол: другой пол» — после страны / возраста иначе читается как ещё одна страна
        parts.push(known.length === genders.length
            ? `пол: ${ADS_FUNNEL_GENDERS.filter((g) => known.includes(g))
                .map((g) => GENDER_WORDS[g])
                .join(', ')}`
            : 'пол не из списка');
    }
    if (Object.keys(when).some((k) => !KNOWN_DIMENSIONS.has(k))) {
        parts.push('условие, которого нет в списке');
    }
    return parts.length > 0 ? parts.join(' · ') : 'без условия';
}
/** «Остальные» без значения: настройка для них не применяется — воронка как настроена. */
export const FUNNEL_OTHERWISE_UNSET = 'как настроено в воронке';
/**
 * Значение с правилами по частям: условие и значение каждого правила по
 * порядку, «остальные». `scalar` — обычное значение словами.
 */
export function targetedValueParts(v, scalar) {
    return {
        rules: v.rules.map((r) => ({ when: whenText(r.when), value: scalar(r.value) })),
        otherwise: v.otherwise === undefined ? null : scalar(v.otherwise),
    };
}
/**
 * Значение с правилами одной строкой: «Германия → выключена; остальные →
 * включена» (без «остальных» — «как настроено в воронке»). `scalar` —
 * обычное значение словами (подпись варианта, список тарифов и т. п.).
 */
export function targetedValueText(v, scalar) {
    const p = targetedValueParts(v, scalar);
    const rules = p.rules.map((r) => `${r.when} → ${r.value}`);
    return [...rules, `остальные → ${p.otherwise ?? FUNNEL_OTHERWISE_UNSET}`].join('; ');
}
// ---------------------------------------------------------------------------
// Форма значения с условиями
// ---------------------------------------------------------------------------
export const FUNNEL_MODE_ALL = 'all';
export const FUNNEL_MODE_RULES = 'rules';
let ruleSeq = 0;
/** Новый ключ правила формы (стабилен при перестановке и удалении соседей). */
export const newRuleId = () => `rule-${++ruleSeq}`;
export const emptyTargetedRule = () => ({
    id: newRuleId(),
    countries: [],
    ageRanges: [],
    genders: [],
    value: undefined,
});
export function isTargetedForm(f) {
    return (typeof f === 'object' &&
        f !== null &&
        'mode' in f &&
        Array.isArray(f.rules));
}
/**
 * Страны списка — в страны правила: без повторов, в порядке добавления, не
 * больше `max`. `dropped` — сколько не влезло.
 */
export function mergeCountries(current, added, max) {
    const next = [...current];
    let dropped = 0;
    for (const c of added) {
        if (next.includes(c))
            continue;
        if (next.length >= max)
            dropped += 1;
        else
            next.push(c);
    }
    return { next, dropped };
}
//# sourceMappingURL=funnel-targeting.js.map